import type { Address } from "./address"
import { associationIdName, fetchDelete, fetchGet, fetchPost, fetchPut } from "./common"
import {writable} from "svelte/store";

export interface UserStore {
    user: User
}

export const userStore = writable<UserStore>({
    user: {}
});

export interface User {
    userId?: number
    honorific?: string
    givenNames?: string
    surname?: string
    emailAddress?: string
    phoneNumber?: string

    commonHonorific?: string
    homeAddress?: Address | null
    businessAddress?: Address | null
    userToken?: string
    
    membershipReceivesTextMessages?: boolean
    membershipReceivesMessages?: boolean
    rememberMe?: boolean
}

export interface Membership {
    isSubscriber: boolean
    isTextSubscriber?: boolean
    contactId?: string
}

export interface Validation {
    field: string
    value?: any
    message?: string
}

export interface Message { 
    subject: string
    content: string
}

export interface HonorificsResponse {
    honorific: string;
}
export interface MobileNumberResponse {
    isNotMobileNumber: boolean
    requiresStartTextMessageToResubscribe: boolean
}

export interface EmailVerification {
    verificationId: string;
}

export interface EmailVerificationProof {
    proof: string;
}

export interface UserIdentity {
    id: number;
    givenNames: string;
    surname: string;
    token: string;
}

export const sanitizeHonorific = async (user: User) => {
    const url = `Users/Honorifics?honorific=${user.honorific}`;
    const response = await fetchGet(url) as HonorificsResponse;
    return response;
}

export const checkUserPhone = async (user: User) => {
    const url = `Users/${user.userToken}/MobileNumber?association=${associationIdName}&mobileNumber=${user.phoneNumber}`;
    const response = await fetchGet(url) as MobileNumberResponse[];
    return response;
}

export const postUser = async (user: User) => {
    const url = `Users?association=${associationIdName}`;
    const response = await fetchPost(url, user) as User;
    return response;
}

export const putUser = async (user: User) => {
    if (user.userToken) {
        const url = `Users/${user.userToken}?association=${associationIdName}`;
        const response = await fetchPut(url, user) as User;
        return response;
    }
    
    return null;
}

export const getUser = async () => {
    const url = `Users/GetRemembered?association=${associationIdName}`;
    const response = await fetchGet(url) as User;
    return response;
}

export const postMemberships = async (user: User) => {
    const url = `Users/${user.userToken}/Memberships?association=${associationIdName}&receivesMessages=${user.membershipReceivesMessages}`;
    const response = await fetchPost(url, null) as any;
    return response;
}
export const postRememberMeCookies = async (user: User) => {
    const url = `Account/RememberMeCookies?user=${user.userToken}`;
    const response = await fetchPost(url, null, true) as any;
    return response;
}
export const deleteRememberMeCookies = async () => {
    const url = `Account/RememberMeCookies`;
    const response = await fetchDelete(url, null, true) as any;
    return response;
}

export const getMemberships = async (user: User) => {
    const url = `Users/${user.userToken}/Memberships?association=${associationIdName}`;
    const response = await fetchGet(url) as Membership;
    return response;
}

export const postValidations = async (user: User) => {
    const url = `Users/${user.userToken}/Validations?field=phoneNumber`;
    const response = await fetchPost(url, user.phoneNumber) as Validation[];
    return response;
}

export const postEmailVerification = async (emailAddress: string) => {
    const url = `EmailOwnershipVerifications?association=${associationIdName}`;
    const response = await fetchPost(url, { emailAddress: emailAddress }) as EmailVerification;
    return response;
}

export const postEmailVerificationProof = async (verificationId: string, verificationCode: string) => {
    const url = `EmailOwnershipVerifications/${verificationId}/Proof`;
    const response = await fetchPost(url, { code: verificationCode }) as EmailVerificationProof;
    return response;
}

export const getIdentities = async (emailAddress: string, ownershipProof?: string) => {
    let url = `Users/Identities?email=${encodeURIComponent(emailAddress)}`;
    if (ownershipProof) {
       url += `&ownershipProof=${ownershipProof}`; 
    }
    const response = await fetchGet(url) as UserIdentity[];
    return response;
}

export async function tryLoadUser(rememberMeChecked: boolean = true): Promise<User> {
    let userInfo: User;
    const userResponse = await getUser();
    if (!userResponse) {
        userInfo = { rememberMe: rememberMeChecked };
        return userInfo;
    }

    userInfo = {
        commonHonorific: userResponse.commonHonorific,
        emailAddress: userResponse.emailAddress,
        givenNames: userResponse.givenNames,
        honorific: userResponse.honorific,
        surname: userResponse.surname,
        userId: userResponse.userId,
        homeAddress: userResponse.homeAddress,
        businessAddress: userResponse.businessAddress,
        phoneNumber: userResponse.phoneNumber,
        userToken: userResponse.userToken,
        rememberMe: rememberMeChecked
    };

    const membershipResponse = await getMemberships(userInfo);
    userInfo.membershipReceivesMessages = membershipResponse.isSubscriber;
    userInfo.membershipReceivesTextMessages = membershipResponse.isTextSubscriber;
    
    return userInfo;
}

export async function resetUser(user: User){
    user.commonHonorific = '';
    user.emailAddress = '';
    user.givenNames = '';
    user.honorific = '';
    user.surname = '';
    user.userId = 0;
    user.homeAddress = null;
    user.businessAddress = null;
    user.phoneNumber = '';
    user.userToken = '';
    user.rememberMe = true;
}